import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Pagination as MuiPagination,
  SvgIcon,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import VerifiedIcon from "@mui/icons-material/Verified";
import { NoConnectionDisplay } from "./NoConnectionDisplay";
import StarIcon from "@mui/icons-material/Star";
import { DESIGN_TOKENS } from "../../styles/theme";
import { useI18n } from "../../i18n";
import { getGoogleAuthUrl, loginUser as requestLogin } from "../../api/services";
import { useAuthentication } from "../../store/AppContext";
import { hasNonEmptyValue, isValidPassword } from "../../utils";
import { rememberGoogleAuthIntent } from "../../pages/AuthCallbackPage";

// ============================================================
// GOOGLE AUTH BUTTON
// ============================================================

interface GoogleAuthButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function GoogleIcon() {
  return (
    <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 20 }}>
      <path
        fill="#4285F4"
        d="M21.6 12.23c0-.78-.07-1.53-.2-2.23H12v4.22h5.37a4.6 4.6 0 0 1-1.99 3.02v2.51h3.22c1.89-1.74 3-4.3 3-7.52Z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.96-.89 6.6-2.42l-3.22-2.51c-.9.6-2.04.95-3.38.95-2.6 0-4.8-1.76-5.59-4.12H3.08v2.59A9.97 9.97 0 0 0 12 22Z"
      />
      <path
        fill="#FBBC05"
        d="M6.41 13.9a6.01 6.01 0 0 1 0-3.8V7.51H3.08a10 10 0 0 0 0 8.98l3.33-2.59Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.98c1.47 0 2.78.5 3.82 1.5l2.85-2.85A9.58 9.58 0 0 0 12 2a9.97 9.97 0 0 0-8.92 5.51l3.33 2.59C7.2 7.74 9.4 5.98 12 5.98Z"
      />
    </SvgIcon>
  );
}

export function GoogleAuthButton({
  label,
  onClick,
  disabled = false,
}: GoogleAuthButtonProps) {
  return (
    <Button
      type="button"
      variant="outlined"
      fullWidth
      size="large"
      startIcon={<GoogleIcon />}
      onClick={onClick}
      disabled={disabled}
      sx={{
        py: 1.35,
        borderColor: DESIGN_TOKENS.colors.border,
        color: DESIGN_TOKENS.colors.textPrimary,
        backgroundColor: DESIGN_TOKENS.colors.surface,
        "&:hover": {
          borderColor: DESIGN_TOKENS.colors.primary,
          backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
        },
      }}
    >
      {label}
    </Button>
  );
}

interface LoginRequiredModalProps {
  description?: string;
  onClose: () => void;
  open: boolean;
  title?: string;
}

export function LoginRequiredModal({
  description = "Zaloguj się, aby skorzystać z tej funkcji.",
  onClose,
  open,
  title = "Ta funkcja wymaga logowania",
}: LoginRequiredModalProps) {
  const { loginUser: setUserLoggedIn, isAuthenticated } = useAuthentication();
  const [emailOrUsername, setEmailOrUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [loginError, setLoginError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open && isAuthenticated) {
      onClose();
    }
  }, [isAuthenticated, onClose, open]);

  React.useEffect(() => {
    if (!open) {
      setEmailOrUsername("");
      setPassword("");
      setLoginError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleLoginSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!hasNonEmptyValue(emailOrUsername) || !hasNonEmptyValue(password)) {
      setLoginError("Wypełnij wszystkie pola.");
      return;
    }

    if (!isValidPassword(password)) {
      setLoginError(
        "Hasło musi mieć minimum 8 znaków, jedną cyfrę, jedną wielką literę, jedną małą literę i jeden znak specjalny.",
      );
      return;
    }

    setIsSubmitting(true);
    setLoginError(null);

    try {
      const authResponse = await requestLogin({
        identifier: emailOrUsername.trim(),
        password,
      });
      setUserLoggedIn(authResponse.user, authResponse.jwt);
      onClose();
    } catch {
      setLoginError("Nieprawidłowy login lub hasło. Spróbuj ponownie.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    rememberGoogleAuthIntent("login");
    window.location.assign(getGoogleAuthUrl());
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, position: "relative" }}>
        <IconButton
          aria-label="Zamknij"
          onClick={onClose}
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ pr: 4 }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {description}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleLoginSubmit} noValidate>
          {loginError && (
            <Box sx={{ mb: 2.5 }}>
              <ErrorDisplay message={loginError} />
            </Box>
          )}

          <TextField
            fullWidth
            label="E-mail lub nazwa użytkownika"
            type="text"
            value={emailOrUsername}
            onChange={(event) => setEmailOrUsername(event.target.value)}
            autoComplete="username"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon
                    sx={{ color: DESIGN_TOKENS.colors.primary, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Hasło"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon
                    sx={{ color: DESIGN_TOKENS.colors.primary, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isSubmitting}
            sx={{ py: 1.5 }}
          >
            {isSubmitting ? "Logowanie..." : "Zaloguj się"}
          </Button>

          <Divider sx={{ my: 2.5 }} />

          <GoogleAuthButton
            label="Zaloguj się przez Google"
            onClick={handleGoogleLogin}
            disabled={isSubmitting}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// LOADING SPINNER
// ============================================================

interface LoadingSpinnerProps {
  message?: string;
  fullHeight?: boolean;
}

export function LoadingSpinner({
  message,
  fullHeight = false,
}: LoadingSpinnerProps) {
  const { t } = useI18n();
  const displayMessage = message ?? t("common.loading");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        py: fullHeight ? 0 : 8,
        height: fullHeight ? "60vh" : "auto",
      }}
    >
      <CircularProgress
        size={40}
        thickness={4}
        sx={{ color: DESIGN_TOKENS.colors.primary }}
      />
      {displayMessage && (
        <Typography variant="body2" color="text.secondary">
          {displayMessage}
        </Typography>
      )}
    </Box>
  );
}

// ============================================================
// ERROR DISPLAY
// ============================================================

interface ErrorDisplayProps {
  message: string;
  onRetry?: () => void;
  fullHeight?: boolean;
}

export function ErrorDisplay({
  message,
  onRetry,
  fullHeight = false,
}: ErrorDisplayProps) {
  const { t } = useI18n();
  const isConnectionError =
    message.includes("Brak połączenia z serwerem") ||
    message.toLowerCase().includes("failed to fetch") ||
    message.toLowerCase().includes("networkerror");

  const is500Error = message.includes("500");

  if (isConnectionError) {
    return <NoConnectionDisplay onRetry={onRetry} />;
  }

  const displayMessage = is500Error ? t("common.serverError") : message;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        py: fullHeight ? 0 : 8,
        height: fullHeight ? "60vh" : "auto",
        textAlign: "center",
        px: 2,
      }}
    >
      <ErrorOutlineIcon
        sx={{ fontSize: 56, color: DESIGN_TOKENS.colors.error, opacity: 0.7 }}
      />
      <Alert severity="error" sx={{ maxWidth: 480, width: "100%" }}>
        {displayMessage}
      </Alert>
      {onRetry && (
        <Button variant="outlined" color="error" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      )}
    </Box>
  );
}

// ============================================================
// EMPTY STATE
// ============================================================

interface EmptyStateProps {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export function EmptyState({
  title,
  description,
  actionLabel,
  onAction,
}: EmptyStateProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        py: 10,
        textAlign: "center",
        px: 2,
      }}
    >
      <SearchOffIcon
        sx={{
          fontSize: 64,
          color: DESIGN_TOKENS.colors.primaryLight,
          opacity: 0.6,
        }}
      />
      <Typography variant="h5" fontWeight={600}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 400 }}
        >
          {description}
        </Typography>
      )}
      {actionLabel && onAction && (
        <Button variant="contained" onClick={onAction} sx={{ mt: 1 }}>
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

// ============================================================
// PAGINATION CONTROL
// ============================================================

interface PaginationControlProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
}

export function PaginationControl({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
}: PaginationControlProps) {
  const { t } = useI18n();
  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * (pageSize ?? 10) + 1;
  const endItem = Math.min(currentPage * (pageSize ?? 10), totalItems ?? 0);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mt: 4,
        pt: 3,
        borderTop: `1px solid ${DESIGN_TOKENS.colors.border}`,
      }}
    >
      {totalItems !== undefined && (
        <Typography variant="body2" color="text.secondary">
          {t("common.displayedResults", {
            start: startItem,
            end: endItem,
            total: totalItems,
          })}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("common.pageOf", { current: currentPage, total: totalPages })}
        </Typography>
        <MuiPagination
          count={totalPages}
          page={currentPage}
          onChange={(_, page) => onPageChange(page)}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
        />
      </Box>
    </Box>
  );
}

// ============================================================
// VERIFIED BADGE
// ============================================================

interface VerifiedBadgeProps {
  isVerified: boolean;
  isHighlighted?: boolean;
}

export function VerifiedBadge({
  isVerified,
  isHighlighted,
}: VerifiedBadgeProps) {
  const { t } = useI18n();
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {isVerified && (
        <Chip
          icon={<VerifiedIcon sx={{ fontSize: "14px !important" }} />}
          label={t("common.verified")}
          size="small"
          sx={{
            backgroundColor: DESIGN_TOKENS.colors.verifiedBackground,
            color: DESIGN_TOKENS.colors.verified,
            fontWeight: 600,
            fontSize: "0.7rem",
            border: `1px solid ${DESIGN_TOKENS.colors.verified}30`,
          }}
        />
      )}
      {isHighlighted && (
        <Chip
          icon={<StarIcon sx={{ fontSize: "14px !important" }} />}
          label={t("common.highlighted")}
          size="small"
          sx={{
            backgroundColor: DESIGN_TOKENS.colors.highlightedBackground,
            color: DESIGN_TOKENS.colors.highlighted,
            fontWeight: 600,
            fontSize: "0.7rem",
            border: `1px solid ${DESIGN_TOKENS.colors.highlighted}30`,
          }}
        />
      )}
    </Box>
  );
}

// ============================================================
// CONTACT ITEM COMPONENT
// ============================================================

interface ContactItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
}

export function ContactItem({ icon, label, value, href }: ContactItemProps) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
      <Box
        sx={{ color: DESIGN_TOKENS.colors.primary, mt: 0.25, flexShrink: 0 }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontSize: "0.68rem",
          }}
        >
          {label}
        </Typography>
        {href ? (
          <Typography
            component="a"
            href={href}
            variant="body2"
            sx={{
              display: "block",
              color: DESIGN_TOKENS.colors.primary,
              fontWeight: 500,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {value}
          </Typography>
        ) : (
          <Typography variant="body2" fontWeight={500}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ============================================================
// SECTION HEADER
// ============================================================

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  centered?: boolean;
}

export function SectionHeader({
  title,
  subtitle,
  centered = false,
}: SectionHeaderProps) {
  return (
    <Box sx={{ mb: 4, textAlign: centered ? "center" : "left" }}>
      <Typography variant="h2" gutterBottom>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="subtitle1"
          sx={{ maxWidth: centered ? 560 : "none", mx: centered ? "auto" : 0 }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

export * from "./ListingCard";
export * from "./NoConnectionDisplay";
export * from "./StatusChips";
export * from "./RouteMap";
export * from "./LocationMap";
export * from "./LocationPickerMap";
export * from "./ImageLightbox";
export * from "./StatusChips";
export * from "./PilotPreferencesManager";
export * from "./MarkdownRenderer";
export * from "./TransportCompanyCard";
export * from "./TransportCompanyFilterSidebar";
export * from "./PilotCard";
export * from "./PilotFilterSidebar";
export * from "./FavoriteButton";
export * from "./ApplyToListingModal";
